import React from "react";
import { store } from "../../../../../stateManager";
import { AppState } from "../../../../../stateManager/store";
import { FormCreationData, FormStack, UNLINK_ASSET } from "../../../../../stateManager/store/formCreation_v2/types";
import { getChildren, copyObj } from "./";
import { Component } from '../../../../../stateManager/store/formCreation_v2/types'

export const getAssetComponent = (assetId: string, componentId: string) => {
  const state: AppState = store.getState();
  const formJson = state.formJson;
  const { assets } = formJson;
  const asset = assets[assetId]?.json;
  const baseComponentId = componentId?.includes('_') ? componentId.split('_')[1] : componentId;
  const component = asset.components[baseComponentId];
  const parentComponent =
    component?.parentId && asset.components[component.parentId];

  return {
    component,
    parentComponent,
  };
};

export const getContainerField = (
  componentId: string,
  parent: FormCreationData
) => {
  const { assetBindPrefix, assetInstanceId, assetId, isAsset, isChildAsset } =
    parent;
  const { components } = getComponents();

  let component = components[componentId];
  let parentComponent: any =
    component?.parentId && components[component.parentId];

  if (isAsset || isChildAsset) {
    const { component: cmp, parentComponent: pCmp } = getAssetComponent(
      assetId,
      componentId
    );
    component = {
      ...cmp,
      id: componentId,
      assetInstanceId,
      assetBindPrefix,
      assetId,
      isChildAsset: true,
    };
    parentComponent = pCmp;
  }

  if(component?.isAsset) {
    return getComponentFromStore(component.id)
  }

  return {
    component,
    parentComponent,
  };
};

export const getComponentFromStore = (componentId: string) => {
  const state: AppState = store.getState();
  const formJson = state.formJson;
  const { assets } = formJson;
  const { components } = getComponents();

  let component: FormCreationData = components[componentId];
  let parentComponent: any =
    component?.parentId && components[component.parentId];

  if (component?.isAsset) {
    const asset = assets[component.assetId]?.json;

    if(!asset || !asset.componentOrder.length) {
      return null
    }

    const [rootId] = asset.componentOrder;
    const { component: assetComponent, parentComponent: pComp } =
      getAssetComponent(component.assetId, rootId);
    component = {
      ...component,
      ...assetComponent,
      id: component.id,
      fields: assetComponent.fields.map((fieldId: string) =>
        concatIds(components[componentId]?.assetInstanceId, fieldId)
      )
    };
    parentComponent = pComp;
  }

  return {
    component,
    parentComponent,
  };
};

export const getComponents = () => {
  const state: AppState = store.getState();
  const formJson = state.formJson;
  let { components, componentOrder, assetEditMode, assets } = formJson;

  if (assetEditMode.isEnabled) {
    const assetId = assetEditMode.assetId;
    let { components: cmps, componentOrder: cmpOrder } = assets[assetId]?.json;
    components = cmps;
    componentOrder = cmpOrder;
  }

  return {
    components,
    componentOrder,
  };
};

export const concatIds = (assetInstanceId: string | undefined, id: string) => {
  return assetInstanceId ? assetInstanceId + "_" + id : id
}

export const addAssetInstancesToForm = (state: FormStack) => {
  for (let key in state.components) {
    const component = state.components[key];
    if (component.isAsset) {
      // get asset json
      const { assetId, assetBindPrefix, assetInstanceId, parentId } = component;
      const asset = state.assets[assetId];

      // if the asset entry is missing, delete the entry from the
      // form source and skip the current iteration
      if(!asset) {
        if (parentId) {
          const parent = state.components[parentId];
          parent.fields = parent.fields.filter((id) => id !== key);
        } else {
          state.componentOrder = state.componentOrder.filter((id) => id !== key);
        }
        delete state.components[key];
        continue;
      }

      // get the root field
      const json = asset?.json
      const [rootId] = json.componentOrder;
      const rootField = json.components[rootId];

      // get children
      const children = getChildren(rootField.fields, json.components);
      children.forEach((id) => {
        const component = json.components[id];
        const newCompId = concatIds(assetInstanceId, id);
        state.components[newCompId] = {
          ...component,
          assetInstanceId,
          assetBindPrefix,
          assetId,
          isChildAsset: true,
          id: concatIds(assetInstanceId, component.id),
          parentId: concatIds(assetInstanceId, rootId),
          fields: component.fields.map((fieldId: string) =>
            concatIds(assetInstanceId, fieldId)
          ),
        };
      });

      state.components[component.id] = {
        ...component,
        ...rootField,
        id: component.id,
        fields: rootField.fields.map((fieldId) =>
          concatIds(assetInstanceId, fieldId)
        ),
      }
    }
  }
};

export const deleteChildAssets = (components: {
  [key: string]: FormCreationData;
}) => {
  for(let key in components) {
    const component = components[key]
    if(component.isChildAsset) {
      delete components[key]
    }
  }
};

export const unlinkAsset = (elementId: string) => {
  const state: AppState = store.getState();
  const formJson = state.formJson;
  const component = formJson.components[elementId];
  const assets = formJson.assets;

  const [, rootId] = elementId.split("_");
  const { components } = assets[component.assetId].json;
  const compCopy = copyObj(components[rootId]);
  const assetInstanceId = component.assetInstanceId;
  const children = getChildren(compCopy.fields, components);

  const unlinkedComponents: Component = {};
  children.concat([rootId]).forEach((id) => {
    const comp = copyObj(components[id]);
    const newId = concatIds(assetInstanceId, id);

    unlinkedComponents[newId] = {
      ...copyObj(components[id]),
      id: newId,
      parentId: comp.parentId
        ? concatIds(assetInstanceId, comp.parentId)
        : comp.parentId,
      fields: comp.fields
        ? comp.fields.map((field: string) => concatIds(assetInstanceId, field))
        : comp.fields,
    };

    if (id === rootId) {
      unlinkedComponents[newId].parentId = component.parentId;
    }
  });

  store.dispatch({
    type: UNLINK_ASSET,
    data: {
      unlinkedComponents,
    },
  });
};

export const getHighlightedText = (text: string, highlight: string) => {
  try {
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return <span> { parts.map((part, i) => 
        <span key={i} style={part.toLowerCase() === highlight.toLowerCase() ? { fontWeight: 'bold', color: "#F1722E" } : {} }>
            { part }
        </span>)
    } </span>;
  } catch(error) {
    console.log(error)
  }
}